<template>
  <div class="field has-addons">
    <p class="control is-expanded">
      <span
        class="input"
        :class="classes"
        readonly
        :disabled="!fieldDefinition.editable || undefined"
        v-on:click="openModal"
        >{{ exportDisplayString }}</span
      >
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable"
    >
      <button
        title="Select an export"
        class="button has-text-primary"
        type="button"
        v-on:click="openModal"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-edit"></i>
      </button>
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable && fieldData.value !== null && !fieldDefinition.required"
    >
      <button
        class="button has-text-danger"
        type="button"
        v-on:click="fieldData.value = null"
        title="Deselect current export"
        :disabled="fieldData.loading || fieldData.value === null || undefined"
      >
        <i class="icon-minus"></i>
      </button>
    </p>
  </div>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>

  <Modal
    v-model="pickerModal"
    title="Select an export"
    is-large
  >
    <div
      class="columns m-0"
      v-if="!fieldDefinition.required"
    >
      <div class="column p-0">
        <label class="label">Use the current state of the project (default)</label>
      </div>
      <div class="column p-0 is-narrow">
        <button
          type="button"
          class="button is-small"
          :class="{ 'is-primary': fieldData.value === null }"
          v-on:click="select(null)"
          :disabled="fieldData.loading || undefined"
        >
          Select
        </button>
      </div>
    </div>
    <label
      class="label"
      v-if="!fieldDefinition.required"
      >Use an existing database export</label
    >
    <template v-if="corpusId">
      <Paginator
        :response="exportStore.corpusExports[corpusId]"
        :loading="paginationLoading"
        singular="export"
        plural="exports"
        v-model:page="page"
        :page-size="10"
      >
        <template v-slot:no-results>
          <div class="notification is-warning mb-2">No existing database exports to display.</div>
        </template>
        <template v-slot:default="{ results }">
          <table class="table is-hoverable is-fullwidth mb-2">
            <thead>
              <tr>
                <th>Creator</th>
                <th v-if="authStore.hasFeature('enterprise')">Source</th>
                <th>State</th>
                <th>Updated</th>
                <th class="is-narrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="corpusExport in results"
                :key="corpusExport.id"
              >
                <td>{{ corpusExport.user.display_name }}</td>
                <td v-if="authStore.hasFeature('enterprise')">{{ corpusExport.source }}</td>
                <td>{{ EXPORT_STATES[corpusExport.state] }}</td>
                <td :title="corpusExport.updated">{{ ago(new Date(corpusExport.updated)) }}</td>
                <td class="is-narrow">
                  <div class="field">
                    <button
                      type="button"
                      class="button is-small"
                      :class="{ 'is-primary': fieldData.value === corpusExport.id }"
                      v-on:click="select(corpusExport)"
                      :disabled="
                        corpusExport.state === 'failed' ||
                        paginationLoading ||
                        fieldData.loading ||
                        undefined
                      "
                      :title="selectTitle(corpusExport)"
                    >
                      Select
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </Paginator>
    </template>
    <template v-slot:footer>
      <!-- No need for footer actions on this component -->
      <template></template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import Modal from '@/components/Modal.vue'
import Paginator from '@/components/Paginator.vue'
import { EXPORT_STATES, UUID_REGEX } from '@/config'
import { ago } from '@/helpers'
import { useAuthStore, useExportStore } from '@/stores'
import type { CorpusExport } from '@/types/export'

import { type FieldModel, type FieldProps, ValidationError, defineValidator } from '.'

const authStore = useAuthStore()
const exportStore = useExportStore()

type Constraint = { type: 'corpus_export'; many: false }

const { fieldDefinition, corpusId } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

const classes = computed(() => ({
  'is-danger': !fieldData.value.valid,
  'is-loading': fieldData.value.loading,
  'is-clickable': fieldDefinition.editable || !!fieldData.value.value,
}))

const exportData = computed(() => {
  if (corpusId === null || fieldData.value.value === null) return null
  // Try to retrieve data from the current page the store.
  const currentPage = exportStore.corpusExports[corpusId]?.results || []
  return currentPage.find((corpusExport) => corpusExport.id === fieldData.value.value)
})

const exportDisplayString = computed<string>(() => {
  if (!fieldData.value.value)
    return fieldDefinition.required ? 'No project export selected' : 'Current state of the project'
  else if (exportData.value) {
    const humanizedDate = ago(new Date(exportData.value.updated))
    return `${humanizedDate} — ${exportData.value.user.display_name}`
  }
  return `Project export ${fieldData.value.value}`
})

function selectTitle(corpusExport: CorpusExport): string {
  if (corpusExport.state === 'failed') return 'This export cannot be selected as it has failed'
  return 'Use this export'
}

const pickerModal = ref(false)
function openModal() {
  // On an editable field, open the corpus export picker modal
  if (fieldDefinition.editable) pickerModal.value = true
}

function select(corpusExport: CorpusExport | null) {
  if (corpusExport === null) {
    fieldData.value.value = null
  } else {
    fieldData.value.value = corpusExport.id
  }
  // Immediately close the modal to save time
  pickerModal.value = false
}

async function validator(id: unknown) {
  if (id === null) return
  if (typeof id !== 'string' || !UUID_REGEX.test(id))
    throw new ValidationError([`${id} is not a valid project export ID`])

  // When this field is loaded without a corpus ID set, we cannot validate anything
  if (!corpusId) return

  // Because we load only one page, we might not find the export and will not be able to tell if it's invalid
  if (!exportData.value) return
  if (exportData.value.state === 'failed') throw new ValidationError(['This export has failed'])
}
const { localErrors } = defineValidator(fieldDefinition, fieldData.value, validator)

const paginationLoading = ref(false)
const page = ref(1)

watch(
  page,
  async (value) => {
    if (paginationLoading.value || !corpusId) return
    paginationLoading.value = true
    try {
      await exportStore.list(corpusId, value)
    } finally {
      paginationLoading.value = false
    }
  },
  // Immediately load the page, so the computed name may contain the information
  { immediate: true },
)
</script>
